// PO ボード（エピック管理ツール）への外部リンクを構築するユーティリティ。
// リファインメントキャンバスは canvasId に PO ボードのエピック ID（`epic-{UUIDv4}` 形式）を
// そのまま受け取れるよう正規表現を緩和済み。本ユーティリティはそのエピック ID をクエリ
// パラメータに付与した PO ボードの URL を組み立てて返す。

/**
 * PO ボードのエピック詳細を URL クエリで開く際に使うクエリ名。
 * PO ボード側 `apps/web/src/app.tsx` の epicDetailSearchParamName と同期させる必要がある。
 */
const PO_BOARD_EPIC_PARAM = "epicId";

/**
 * canvasId が PO ボードのエピックに紐付くかを判定する。
 * PO ボード側のエピック ID 生成は `epic-{UUIDv4}` の 41 文字固定。
 * 厳密に UUID 形式まで検証はせず、`epic-` プレフィックス + 6 文字以上の英数ハイフンで簡易判定する。
 */
export function isEpicLinkedCanvasId(canvasId: string): boolean {
  return /^epic-[A-Za-z0-9-]{6,}$/.test(canvasId);
}

/**
 * 自サービス origin から PO ボードの origin を導出する。
 *
 * @param serviceOrigin リファインメントキャンバス自身の origin（例: window.location.origin）
 * @param overrideOrigin 環境変数等で明示指定された PO ボード origin（任意）
 * @returns PO ボード origin。導出できない場合は null。
 *
 * 派生規則: ホスト名先頭の `refinement.` を `po-board.` に置換する。
 *   - refinement.demo.ours.connect.meidensha.co.jp → po-board.demo.ours.connect.meidensha.co.jp
 *   - refinement.ours.connect.meidensha.co.jp → po-board.ours.connect.meidensha.co.jp
 * 該当しない（localhost や未知のホスト）場合は overrideOrigin を優先し、それも無ければ null を返す。
 */
export function getPoBoardOrigin(
  serviceOrigin: string | undefined,
  overrideOrigin?: string | undefined,
): string | null {
  // override も URL として妥当でなければ null にフォールバックする。
  // 不正な値が混入したまま `new URL(origin)` を後段で呼ぶと例外で UI が落ちるため、
  // 必ずここで弾く（codex セルフレビュー指摘の反映）。
  const validatedOverride = validateOriginUrl(overrideOrigin);
  if (validatedOverride !== null) {
    return validatedOverride;
  }

  if (serviceOrigin === undefined || serviceOrigin.length === 0) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(serviceOrigin);
  } catch {
    return null;
  }

  if (!url.hostname.startsWith("refinement.")) {
    return null;
  }

  const newHostname = `po-board.${url.hostname.slice("refinement.".length)}`;
  url.hostname = newHostname;

  return normalizeOrigin(url.origin);
}

function validateOriginUrl(value: string | undefined): string | null {
  if (value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    // `file:` / `javascript:` / `data:` などのスキームは origin が "null" 文字列となり、
    // 後段で `${origin}/?epicId=${...}` を組み立てると "null/?..." の壊れた URL になるため、
    // http / https のみを許可する。
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    if (parsed.origin === "null") {
      return null;
    }
    return normalizeOrigin(parsed.origin);
  } catch {
    return null;
  }
}

/**
 * 対象 canvasId に紐付く PO ボードのエピック詳細 URL を組み立てる。
 * canvasId がエピック紐付き形式でない、または PO ボード origin を解決できない場合は null を返す。
 */
export function buildPoBoardEpicUrl(
  canvasId: string,
  serviceOrigin: string | undefined,
  overrideOrigin?: string | undefined,
): string | null {
  if (!isEpicLinkedCanvasId(canvasId)) {
    return null;
  }

  const origin = getPoBoardOrigin(serviceOrigin, overrideOrigin);
  if (origin === null) {
    return null;
  }

  const url = new URL(origin);
  url.searchParams.set(PO_BOARD_EPIC_PARAM, canvasId);
  return url.toString();
}

function normalizeOrigin(value: string | undefined): string | null {
  if (value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed.replace(/\/$/u, "");
}
